export enum WAFProvider {
  Cloudflare = 'cloudflare',
}

/**
 * The settled outcome of a clearance flow, named by its real producers:
 * - Solved: the challenge was cleared. May carry the `cleared-by-sibling` detail when this tab
 *   short-circuited on a sibling tab's recent solve instead of solving itself.
 * - Error: the clearance machinery failed unexpectedly (`provider-unexpected-error`). The `cancelled` detail
 *   is the provider's record-free teardown, produced only during the manager's post-settle teardown, so a
 *   settled flow never actually carries it.
 * - Unavailable: no solve could complete — either none could run (`provider-not-registered`, `missing-sitekey`,
 *   `widget-unsupported-in-browser`, `widget-unavailable`, `widget-render-crash`) or the interactive promotion
 *   the solve required was withheld (`promotion-withheld-failure-showing`).
 *
 * There is no timeout outcome: a clearance runs until one of these, however long it takes.
 * The terminal-UI affordance is a downstream decision and is deliberately not encoded here.
 */
export enum WAFClearanceResolveState {
  Solved = 'solved',
  Error = 'error',
  Unavailable = 'unavailable',
}

/**
 * Bounded reason token; never a URL, request body, auth token, or cf_clearance value. Later stack steps
 * extend this enum with their own tokens rather than passing free-form strings into telemetry.
 */
export enum WAFClearanceDetail {
  ProviderNotRegistered = 'provider-not-registered',
  ProviderUnexpectedError = 'provider-unexpected-error',
  MissingSitekey = 'missing-sitekey',
  Cancelled = 'cancelled',
  // The interactive challenge wanted to be shown but a terminal failure was already on screen, so the promotion
  // was withheld. The clearance settles Unavailable with this so it ends instead of waiting indefinitely, with a
  // non-browser-interference reason the monitors can single out (and keep off the paging terminal-failure ratio).
  PromotionWithheldFailureShowing = 'promotion-withheld-failure-showing',
  WidgetUnsupportedInBrowser = 'widget-unsupported-in-browser',
  WidgetUnavailable = 'widget-unavailable',
  WidgetRenderCrash = 'widget-render-crash',
  // A Solved detail (not a failure): a sibling tab solved and minted the shared cookie, so this tab settled
  // optimistically on it and lets its parked requests replay on that cookie. Reached either by
  // short-circuiting before solving (a fresh durable hint) or by a real-time broadcast cutting an in-flight
  // solve short (including, at most once until a replay validates, a showing interactive one) — so a flow
  // carrying this detail may itself have invoked the provider first.
  ClearedBySibling = 'cleared-by-sibling',
}

// The clearance details the invisible widget host may settle; manager/provider-owned reasons
// (missing-sitekey, cancelled, provider-*, promotion-withheld-failure-showing) are deliberately excluded
// from settleWidgetFailure.
export type WAFWidgetFailureDetail =
  | WAFClearanceDetail.WidgetUnsupportedInBrowser
  | WAFClearanceDetail.WidgetUnavailable
  | WAFClearanceDetail.WidgetRenderCrash;

// Terminal failures the rate monitors exclude from the failure ratio, so telemetry logs them at warn rather
// than an error per challenged request: the structural widget-unsupported-in-browser floor and the
// promotion-withheld policy decline (settled Unavailable, never via settleWidgetFailure). ReadonlySet so no
// consumer can reclassify alert-relevant events.
// Also consumed by isCircuitCountableFailure in services/waf/circuitBreaker.ts, so a member added here is also
// excluded from the tab circuit breaker's failure count.
// KEEP_IN_SYNC sinatra/generated_monitors/weave_engineering/waf_challenge_monitors.tf: every detail here has a
// matching !detail:<token> exclusion (via local.waf_clearable_detail_exclusions), asserted by
// telemetryMonitorSync.test.ts; move them together.
export const EXPECTED_TERMINAL_DETAILS: ReadonlySet<WAFClearanceDetail> = new Set<WAFClearanceDetail>([
  WAFClearanceDetail.WidgetUnsupportedInBrowser,
  WAFClearanceDetail.PromotionWithheldFailureShowing,
]);

export interface WAFClearanceResult {
  state: WAFClearanceResolveState;
  detail?: WAFClearanceDetail;
}

export interface WAFProviderClearanceResult {
  state: WAFClearanceResolveState;
  detail?: WAFClearanceDetail;
  /** Solved only: whether the WAF vendor reported setting its edge clearance cookie; absent when it did not say. */
  clearanceCookieIssued?: boolean;
}

/** Where a clearance stood when it turned slow. */
export enum WAFClearanceStage {
  ScriptNotLoaded = 'script-not-loaded',
  WidgetNotRendered = 'widget-not-rendered',
  AwaitingWidgetToken = 'awaiting-widget-token',
  // The widget holds a token but has not confirmed the clearance cookie. Turnstile only calls back once its
  // same-origin cookie-redemption request returns, and that request has no timeout.
  AwaitingClearanceCookie = 'awaiting-clearance-cookie',
}

export interface WAFChallengeProvider {
  readonly provider: WAFProvider;
  /** Resolve 'error'/'unavailable' rather than rejecting; a rejection is treated as an unexpected crash ('provider-unexpected-error'). */
  requestClearance(): Promise<WAFProviderClearanceResult>;
  /** Where the in-flight clearance has got to; undefined when idle. */
  describeStage?(): WAFClearanceStage | undefined;
  /**
   * Idempotent no-op when idle; the manager calls it on every settle to tear the recovery step down. Required,
   * not optional: it is the only teardown for a manager-side settle (a sibling tab's clear), so a provider
   * missing it would strand an app-blocking interactive modal with no way out.
   */
  cancelClearance(): void;
}

export interface WAFDetectableResponse {
  status: number;
  /** Header keys must be lowercased by the caller; detectors match on lowercase names. */
  headers: Record<string, string | undefined>;
}

export interface WAFChallengeDetector {
  readonly provider: WAFProvider;
  /** Origin-scoping is the caller's responsibility, applied before matches() runs. */
  matches(response: WAFDetectableResponse): boolean;
}

// KEEP_IN_SYNC sinatra/generated_monitors/weave_engineering/waf_challenge_monitors.tf: these phase/outcome
// tokens (and the WAFClearanceDetail tokens above) are the phase:/outcome:/detail: filters the WAF monitors
// alert on, so renaming a value here must update those filters (and vice versa). `failure-modal` carries the
// info-level modal-lifecycle events (shown/reload), kept off `terminal` so it never inflates the
// terminal-failure monitors; the slow-notice and interactive-help events ride `clearance`.
export enum WAFTelemetryPhase {
  Detected = 'detected',
  Clearance = 'clearance',
  Terminal = 'terminal',
  FailureModal = 'failure-modal',
}

/**
 * Telemetry-only outcomes that are not clearance resolve states: detection/terminal-phase signals, the
 * non-settling widget sub-events, the clearance-phase interactive edges (widget-needs-interaction,
 * widget-after-interactive) and slow signal, and the failure-modal lifecycle events
 * (FailureModalShown/Reload). Kept separate from WAFClearanceResolveState so resolve states stay
 * assignable to WAFTelemetryOutcome.
 * ReplaysExhausted and RecoveryCircuitOpen also participate in WAFTerminalFailureOutcome (the recovery loop
 * raises each without a resolve state).
 */
export enum WAFTelemetryOutcomeExtra {
  Recovered = 'recovered',
  Challenge = 'challenge',
  ReplaysExhausted = 'replays-exhausted',
  OpaqueNetwork = 'opaque-network',
  // Enter edge of the interactive window: the invisible widget asked for human interaction. The host promotes
  // the docked widget into the visible modal UNLESS a terminal failure is already on screen, in which case the
  // clearance settles Unavailable with the PromotionWithheldFailureShowing detail instead — so this event
  // counts asks, not shows.
  WidgetNeedsInteraction = 'widget-needs-interaction',
  // The gate re-read after a solve failed (recovery disabled, or the credential session was superseded
  // mid-solve), so the replay was abandoned rather than sent with stale credentials. A safety decline,
  // not a failure to clear — emitted at phase:detected so it stays out of the terminal-failure ratio.
  GateClosed = 'gate-closed',
  WidgetError = 'widget-error',
  // Exit edge of the interactive window: the widget returned to invisible processing after interaction
  // (pairs with a promoted WidgetNeedsInteraction).
  WidgetAfterInteractive = 'widget-after-interactive',
  // A challenge arrived but cross-tab coordination could not run — localStorage or BroadcastChannel is
  // unavailable/failing — so the tab fell back to a per-tab solve. One shared session latch covers both
  // causes (first failure in a session wins), so it is a "degraded somehow" heads-up at phase:detected, not a
  // per-cause signal.
  CrossTabUnavailable = 'cross-tab-unavailable',
  // A phase:terminal outcome the recovery loop raises when the tab-level circuit breaker is open: recovery has
  // been failing across requests in this tab, so the loop skips the solve and surfaces the failure directly
  // rather than spinning widgets against a persistently-broken edge.
  RecoveryCircuitOpen = 'recovery-circuit-open',
  // Fired once per distinct terminal failure surfaced (not per modal open); a hide→re-show of the same
  // pending failure does not re-emit.
  FailureModalShown = 'failure-modal-shown',
  FailureModalReload = 'failure-modal-reload',
  // Clearance phase: still solving invisibly WAF_SLOW_CLEARANCE_DELAY_MS after it started or after an interactive
  // challenge ended. Not terminal, and can recur within one clearance.
  Slow = 'slow',
  SlowNoticeReload = 'slow-notice-reload',
  // Clearance phase: the widget has the Turnstile script, scriptLoadDurationMs after the clearance started.
  ScriptLoaded = 'script-loaded',
  // The interactive challenge stayed on screen for WAF_INTERACTIVE_HELP_DELAY_MS without clearing, so the overlay
  // offered a reload (shown), and the user took it (reload).
  InteractiveHelpShown = 'interactive-help-shown',
  InteractiveHelpReload = 'interactive-help-reload',
}

export type WAFTelemetryOutcome = WAFClearanceResolveState | WAFTelemetryOutcomeExtra;

/**
 * The terminal, non-solved outcome surfaced to the failure modal: any settled resolve state other than Solved,
 * or one of the recovery-loop signals raised without a resolve state (the replay-budget exhaustion, or the tab
 * circuit breaker being open).
 */
export type WAFTerminalFailureOutcome =
  | Exclude<WAFClearanceResolveState, WAFClearanceResolveState.Solved>
  | WAFTelemetryOutcomeExtra.ReplaysExhausted
  | WAFTelemetryOutcomeExtra.RecoveryCircuitOpen;

/**
 * This request's role in the single-flight clearance it took part in: `solver` if it created the shared
 * clearance, `waiter` if it joined one already in flight. Reported as a telemetry/RUM dimension, and (for
 * `solver`) read as one condition on the solver-only behaviors: the cross-tab clearance signal to sibling tabs
 * (the durable hint write and the live broadcast) and the circuit breaker's failure count — so the
 * `solver`/`waiter` split is load-bearing behavior, not just a dashboard dimension.
 * Only requests that were themselves challenged carry it (those merely held by the request-interceptor gate
 * emit no terminal event). It reflects the request's most recent clearance round — for `replays-exhausted`,
 * which attempts none, the last completed round. A request that created the shared clearance is `solver` even
 * when it settled `cleared-by-sibling` (whether by short-circuiting on a fresh hint before solving, or by a
 * broadcast cutting its own in-flight solve short); a request that joined an in-flight clearance later settled
 * by a sibling broadcast carries `cleared-by-sibling` as a `waiter`.
 */
export enum WAFClearanceRole {
  Solver = 'solver',
  Waiter = 'waiter',
}

/**
 * Which client transport drove the recovery, so ai-chat recoveries (a separate streaming fetch) are
 * dashboard-separable from the axios instance's. Low-cardinality; both transports share one recovery loop.
 */
export enum WAFRecoveryTransport {
  Axios = 'axios',
  AiChat = 'ai-chat',
}

export interface WAFTelemetryEvent {
  phase: WAFTelemetryPhase;
  outcome: WAFTelemetryOutcome;
  wafProvider?: WAFProvider;
  transport?: WAFRecoveryTransport;
  durationMs?: number;
  /**
   * `document.visibilityState` — the DOM Page Visibility API, reporting whether the browser tab is
   * foregrounded (`visible`) or backgrounded (`hidden`). This is the page/tab state, not the challenge
   * widget's own visibility.
   */
  pageVisibilityState?: DocumentVisibilityState;
  replayCount?: number;
  clearanceRole?: WAFClearanceRole;
  /** Whether the clearance ever entered the interactive (visible-modal) posture; a bounded split dimension. */
  wasEverInteractive?: boolean;
  /**
   * The bounded reason for this event's own `outcome` — except on `phase:failure-modal` events, where the
   * outcome is a modal-lifecycle signal and `detail` instead qualifies the `terminalOutcome` that triggered it.
   */
  detail?: WAFClearanceDetail;
  /** Provider-native error code (e.g. a Cloudflare Turnstile client error code); a bounded diagnostic, never PII. */
  providerErrorCode?: string;
  /**
   * The terminal failure that triggered this event; never `recovered`, even though `recovered` is itself a
   * terminal-phase outcome.
   */
  terminalOutcome?: WAFTerminalFailureOutcome;
  /** True when the challenged request is a login-flow endpoint, so signin recovery can be watched
   *  separately from product traffic on the dashboard. Low-cardinality (boolean). */
  isLoginFlowRequest?: boolean;
  /** Slow events only: where the solve had got to. */
  clearanceStage?: WAFClearanceStage;
  /** Solved clearances only. */
  clearanceCookieIssued?: boolean;
  wasEverSlow?: boolean;
  scriptLoadDurationMs?: number;
  /** True when the page already had the Turnstile script, so scriptLoadDurationMs measured no download. */
  wasScriptAlreadyLoaded?: boolean;
}
